import { Component, OnInit, AfterViewInit, OnDestroy, ElementRef, NgZone, signal } from '@angular/core';
import { Location } from '@angular/common';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { ApiService } from '../services/api.service';
import { AuthService } from '../services/auth.service';
import { Tooltip } from 'bootstrap';
import { ToastService } from '../components/toast.service';

@Component({
  standalone: true,
  selector: 'app-festival',
  imports: [CommonModule, FormsModule, RouterModule],
  styles: [
    `
    .icon-spotify { width: 18px; height: 18px; vertical-align: middle; }
    .spotify-link img { display: inline-block; }
    /* Unstyled button for table header sorting: keep th typography */
    .th-sort { background: none; border: 0; padding: 0; margin: 0; font: inherit; color: inherit; font-weight: inherit; cursor: pointer; }
    .th-sort:focus-visible { outline: 2px solid var(--bs-primary); outline-offset: 2px; }
    /* Dark mode: ensure select caret is dark even in normal (non-compact) mode */
    :host-context([data-bs-theme="dark"]) .rating-select.form-select {
      --bs-form-select-bg-img: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='%23212529' d='M3.204 5.5L8 10.296 12.796 5.5 14 6.704 8 12.704 2 6.704z'/%3e%3c/svg%3e");
      background-image: var(--bs-form-select-bg-img);
    }
    /* Let notes column take remaining width; allow name to grow until it fits on one line */
    .col-name { white-space: nowrap; width: auto; }
    .col-notes { width: 100%; }
    .col-spotify { white-space: nowrap; width: 1%; }
    .rating-select-wrapper { position: relative; }
    .rating-select-wrapper .rating-display { display: none; }
    /* Normal (>= lg): make rating select wide enough for full label */
    @media (min-width: 992px) {
      .col-rating { white-space: nowrap; }
      .col-rating .rating-select.form-select.form-select-sm {
        width: auto;
        min-width: 24ch;
      }
    }
    /* Match Bootstrap's lg breakpoint (992px) */
    @media (max-width: 991.98px) {
      /* In compact mode allow band name to wrap as needed */
      .col-name { white-space: normal; }
      .rating-select-wrapper .rating-display {
        display: flex;
        position: absolute;
        inset: 0;
        align-items: center;
        justify-content: flex-start;
        pointer-events: none;
        padding-left: 0.5rem; /* align with .form-select-sm */
        padding-right: 2rem; /* leave room for arrow */
        color: inherit;
        opacity: 1;
        font-weight: 600;
      }
      .rating-select-wrapper .form-select.form-select-sm {
        color: transparent; /* hide selected text when compact */
        text-shadow: none;
        min-width: 2.75rem; /* compact width to fit one digit */
        width: 2.75rem;
        padding-left: 0.5rem;
        padding-right: 1.5rem; /* keep arrow visible */
      }
      /* Keep selected text hidden even on focus; always show overlay number */
      /* In dark mode ensure contrast when a light rating background is applied */
      :host-context([data-bs-theme="dark"]) .rating-select-wrapper .rating-display.rating-1,
      :host-context([data-bs-theme="dark"]) .rating-select-wrapper .rating-display.rating-2,
      :host-context([data-bs-theme="dark"]) .rating-select-wrapper .rating-display.rating-3,
      :host-context([data-bs-theme="dark"]) .rating-select-wrapper .rating-display.rating-4,
      :host-context([data-bs-theme="dark"]) .rating-select-wrapper .rating-display.rating-5 {
        color: #212529 !important;
      }
      /* Force dark dropdown arrow on light rating backgrounds in dark mode */
      :host-context([data-bs-theme="dark"]) .rating-select-wrapper .form-select.form-select-sm.rating-1,
      :host-context([data-bs-theme="dark"]) .rating-select-wrapper .form-select.form-select-sm.rating-2,
      :host-context([data-bs-theme="dark"]) .rating-select-wrapper .form-select.form-select-sm.rating-3,
      :host-context([data-bs-theme="dark"]) .rating-select-wrapper .form-select.form-select-sm.rating-4,
      :host-context([data-bs-theme="dark"]) .rating-select-wrapper .form-select.form-select-sm.rating-5 {
        --bs-form-select-bg-img: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='%23212529' d='M3.204 5.5L8 10.296 12.796 5.5 14 6.704 8 12.704 2 6.704z'/%3e%3c/svg%3e");
        background-image: var(--bs-form-select-bg-img);
      }
    }
    `
  ],
  template: `
<div class="d-flex align-items-center justify-content-between mb-3">
  <button class="btn btn-sm btn-outline-secondary" (click)="goBack()">←<span class="d-none d-lg-inline"> Zurück</span></button>
  <h5 class="mb-0">{{ festivalName || festivalId }}</h5>
  <div class="d-flex align-items-center gap-2">
    <button class="btn btn-sm btn-outline-secondary" (click)="toggleSettings()" title="Einstellungen">⚙️</button>
  </div>
  
</div>

<div class="card mb-3" *ngIf="festivalId && showSettings">
  <div class="card-body">
    <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
      <div class="d-flex align-items-center gap-2">
        <strong>Vergleiche</strong>
        <span class="badge text-bg-light border">max. 5</span>
      </div>
      <div class="d-flex align-items-center gap-2">
        <a *ngIf="isLoggedIn" class="btn btn-sm btn-primary" [routerLink]="['/settings']" [queryParams]="{ festivalId: festivalId }">Eigene Freigabe erstellen</a>
      </div>
    </div>

    <label class="form-label small mb-1">Vergleich hinzufügen</label>
    <div class="input-group input-group-sm mb-2">
      <span class="input-group-text">🔗</span>
      <input class="form-control" [(ngModel)]="compareAliasInput" placeholder="Alias oder Token">
      <span class="input-group-text">🏷️</span>
      <input class="form-control" [(ngModel)]="compareLabelInput" placeholder="Label (optional)">
      <button class="btn btn-outline-primary" (click)="addComparison()" [disabled]="!compareAliasInput || comparisons.length >= 5">Hinzufügen</button>
    </div>
    <div class="text-danger small" *ngIf="compareError">{{ compareError }}</div>
    <div class="form-text mb-2">Füge einen Alias oder Token hinzu, um die Bewertungen anderer anzuzeigen. Optional ein kurzes Label vergeben.</div>

    <div class="mt-2" *ngIf="comparisons.length > 0">
      <div class="small text-muted mb-1">Aktive Vergleiche</div>
      <div class="d-flex flex-wrap gap-2">
        <span class="badge bg-light text-dark border d-flex align-items-center gap-2" *ngFor="let c of comparisons; let i = index" [attr.title]="c.alias">
          <span>{{ c.label || c.alias }}</span>
          <button class="btn btn-sm btn-link p-0" (click)="removeComparison(i)" title="Entfernen">✖️</button>
        </span>
      </div>
    </div>

    

    <div class="mt-2" *ngIf="!isLoggedIn">
      <span class="small text-muted">Melde dich an, um eigene Freigaben zu erstellen.</span>
    </div>
  </div>
</div>

<div class="table-responsive" *ngIf="festivalId">
  <div *ngIf="isLoading" class="d-flex align-items-center gap-2 text-muted mb-2"><span class="spinner-border spinner-border-sm"></span> Lädt Bands...</div>
  <table class="table align-middle">
    <thead>
      <tr>
        <th class="col-name">
          <button class="th-sort" (click)="toggleSort('name')" title="Nach Name sortieren">
            Band
            <span class="ms-1" *ngIf="sortMode.startsWith('name-')">
              <i class="bi" [ngClass]="sortMode === 'name-asc' ? 'bi-sort-down-alt' : 'bi-sort-down'"></i>
            </span>
          </button>
        </th>
        <th class="col-spotify"></th>
        <th class="col-rating">
          <button class="th-sort" (click)="toggleSort('rating')" title="Nach Bewertung sortieren" aria-label="Wertung sortieren">
            <span class="d-none d-lg-inline">Wertung</span>
            <span class="d-inline d-lg-none">Wert</span>
            <span class="ms-1" *ngIf="sortMode.startsWith('rating-')">
              <i class="bi" [ngClass]="sortMode === 'rating-asc' ? 'bi-sort-down-alt' : 'bi-sort-down'"></i>
            </span>
          </button>
        </th>
        <th *ngFor="let c of comparisons">
          <span class="d-none d-lg-inline">{{ c.label || 'Vergleich' }}</span>
          <span class="d-inline d-lg-none">{{ compShort(c) }}</span>
        </th>
        <th class="col-notes">Notiz</th>
      </tr>
    </thead>
    <tbody>
      <tr *ngIf="!isLoading && getSortedBands().length === 0">
        <td colspan="100%" class="text-center text-muted">Keine Bands gefunden.</td>
      </tr>
      <tr *ngFor="let b of getSortedBands()">
        <td class="col-name">{{ b.name }}</td>
        <td class="col-spotify">
          <a *ngIf="b.spotifyArtistId"
             [href]="'spotify:artist:' + b.spotifyArtistId"
             title="Spotify öffnen" class="spotify-link"
             (click)="openSpotifyArtist($event, b.spotifyArtistId)">
            <img src="assets/spotify.svg" alt="Spotify" class="icon-spotify" />
          </a>
        </td>
        <td class="col-rating">
          <div class="rating-select-wrapper">
            <select class="form-select form-select-sm rating-select" [ngClass]="ratingClass(ratingsMap[b.bandId].rating)" [(ngModel)]="ratingsMap[b.bandId].rating" (ngModelChange)="onRatingChange(bandId(b))" (change)="onSelectBlur($event)">
              <option [ngValue]="undefined">–</option>
              <option *ngFor="let n of [1,2,3,4,5]" [ngValue]="n">{{ n }} - {{ labels[n-1] }}</option>
            </select>
            <span class="rating-display" [ngClass]="ratingBadgeClass(ratingsMap[b.bandId].rating)" aria-hidden="true">{{ ratingsMap[b.bandId].rating ?? '–' }}</span>
          </div>
        </td>
        <td class="text-center" *ngFor="let c of comparisons"
            [attr.title]="c.map[b.bandId]?.note || null"
            [attr.data-bs-toggle]="c.map[b.bandId]?.note ? 'tooltip' : null"
            data-bs-placement="top"
            data-bs-custom-class="fb-tooltip">
          <ng-container *ngIf="c.map[b.bandId] as cr; else noComp">
            <ng-container *ngIf="cr.rating != null; else noRating">
              <span class="rating-badge" [ngClass]="[ratingBadgeClass(cr.rating), cr.note ? 'text-decoration-underline' : '']">{{ cr.rating }}</span>
            </ng-container>
            <ng-template #noRating><span class="text-body-secondary">–</span></ng-template>
          </ng-container>
          <ng-template #noComp><span class="text-body-secondary">–</span></ng-template>
        </td>
        <td class="col-notes">
          <ng-container *ngIf="!isEditing(b.bandId); else editTpl">
            <div class="d-flex align-items-center gap-2">
              <span class="text-body-secondary" *ngIf="!ratingsMap[b.bandId].note">–</span>
              <span *ngIf="ratingsMap[b.bandId].note">{{ ratingsMap[b.bandId].note }}</span>
              <button class="btn btn-link btn-sm p-0" (click)="startEdit(b.bandId)" title="Notiz bearbeiten">✏️</button>
            </div>
          </ng-container>
          <ng-template #editTpl>
            <textarea class="form-control form-control-sm note"
                      [(ngModel)]="ratingsMap[b.bandId].note"
                      (blur)="stopEdit(b.bandId, true)"
                      (keydown.enter)="stopEdit(b.bandId, true)"
                      (keydown.escape)="stopEdit(b.bandId, false)"
                      autofocus></textarea>
          </ng-template>
        </td>
        
      </tr>
    </tbody>
  </table>
</div>
  `
})
export class FestivalComponent implements OnInit, AfterViewInit, OnDestroy {
  festivalId: string = '';
  festivalName = '';
  bands = signal<any[]>([]);
  ratingsMap: Record<string, { rating?: number; note?: string }> = {};
  labels: string[] = ['geht gar nicht', 'eher nicht', 'weiß ich noch nicht', 'kann man mal hören', 'unbedingt anhören'];
  private editingNotes: Record<string, boolean> = {};
  sortMode: 'name-asc' | 'name-desc' | 'rating-asc' | 'rating-desc' = 'name-asc';
  // Mehrfach-Vergleiche (bis zu 5)
  compareAliasInput = '';
  compareLabelInput = '';
  comparisons: { alias: string; label?: string; map: Record<string, { rating?: number; note?: string }> }[] = [];
  compareError = '';
  showSettings = false;
  isLoggedIn = false;
  isLoading = false;

  private tooltipInstances: Tooltip[] = [];
  private userSub?: any;

  constructor(private api: ApiService, private auth: AuthService, private route: ActivatedRoute, private elRef: ElementRef<HTMLElement>, private zone: NgZone, private location: Location, private toasts: ToastService) {}

  async ngOnInit() {
    this.route.paramMap.subscribe(async params => {
      const id = params.get('id');
      if (!id) return;
      this.festivalId = id;
      // Optional: Namen anzeigen
      try {
        const list = await this.api.listFestivals();
        const found = list.find((f: any) => f.id === id);
        this.festivalName = found?.name || '';
      } catch {}
      await this.loadData();
      // Persistierte Vergleiche laden (V2: Liste). Fallback: altes Single-Format migrieren.
      try {
        const listRaw = localStorage.getItem(`fb-compare-list-${this.festivalId}`);
        if (listRaw) {
          const list: { alias: string; label?: string }[] = JSON.parse(listRaw);
          await this.loadStoredComparisons(list);
        } else {
          const oldAlias = localStorage.getItem(`fb-compare-${this.festivalId}`);
          if (oldAlias) {
            const oldLabel = localStorage.getItem(`fb-compare-label-${this.festivalId}`) || '';
            const list = [{ alias: oldAlias, label: oldLabel || undefined }];
            await this.loadStoredComparisons(list);
            // Migration speichern und alte Schlüssel entfernen
            this.persistComparisons();
            localStorage.removeItem(`fb-compare-${this.festivalId}`);
            localStorage.removeItem(`fb-compare-label-${this.festivalId}`);
          }
        }
      } catch {}
    });

    const user = await this.auth.currentUser();
    this.isLoggedIn = !!user;
    if (user) {
      const s = await this.api.userSettingsGet();
      if (Array.isArray(s?.labels)) this.labels = s.labels;
    }
    this.userSub = this.auth.userChanges.subscribe(async u => {
      this.isLoggedIn = !!u;
      if (u) {
        try {
          const s = await this.api.userSettingsGet();
          if (Array.isArray(s?.labels)) this.labels = s.labels;
        } catch {}
      }
    });
  }

  ngAfterViewInit() {
    this.scheduleInitTooltips();
  }

  ngOnDestroy() {
    this.destroyTooltips();
    if (this.userSub && typeof this.userSub.unsubscribe === 'function') this.userSub.unsubscribe();
  }

  private async loadData() {
    if (!this.festivalId) return;
    this.isLoading = true;
    try {
      this.bands.set(await this.api.listBands(this.festivalId));
      const ratings = await this.api.getRatings(this.festivalId);
      this.ratingsMap = {};
      this.editingNotes = {};
      this.comparisons = [];
      this.scheduleInitTooltips();
      for (const r of ratings) this.ratingsMap[r.bandId] = { rating: r.rating, note: r.note };
      for (const b of this.bands()) {
        if (!this.ratingsMap[b.bandId]) this.ratingsMap[b.bandId] = {};
        this.editingNotes[b.bandId] = false;
      }
    } finally {
      this.isLoading = false;
    }
  }

  bandId(b: any) { return b.bandId; }

  async onRatingChange(bandId: string) {
    if (!this.festivalId) return;
    const r = this.ratingsMap[bandId];
    await this.api.updateRating(this.festivalId, bandId, { rating: r.rating, note: r.note });
  }

  isEditing(bandId: string): boolean {
    return !!this.editingNotes[bandId];
  }

  startEdit(bandId: string) {
    this.editingNotes[bandId] = true;
  }

  async stopEdit(bandId: string, save: boolean) {
    if (save) {
      await this.onRatingChange(bandId);
    }
    this.editingNotes[bandId] = false;
  }

  toggleSort(field: 'name' | 'rating') {
    if (this.sortMode.startsWith(field)) {
      this.sortMode = (this.sortMode.endsWith('asc') ? `${field}-desc` : `${field}-asc`) as any;
    } else {
      this.sortMode = `${field}-asc` as any;
    }
  }

  getSortedBands() {
    const list = [...this.bands()];
    const nameAsc = (a: any, b: any) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
    const byRating = (a: any, b: any, desc: boolean) => {
      const ra = (this.ratingsMap[a.bandId]?.rating ?? 0) as number;
      const rb = (this.ratingsMap[b.bandId]?.rating ?? 0) as number;
      const diff = ra - rb;
      if (diff !== 0) return desc ? -diff : diff;
      return nameAsc(a, b);
    };

    switch (this.sortMode) {
      case 'name-asc':
        return list.sort(nameAsc);
      case 'name-desc':
        return list.sort((a, b) => -nameAsc(a, b));
      case 'rating-asc':
        return list.sort((a, b) => byRating(a, b, false));
      case 'rating-desc':
        return list.sort((a, b) => byRating(a, b, true));
      default:
        return list.sort(nameAsc);
    }
  }

  private async loadStoredComparisons(list: { alias: string; label?: string }[]) {
    this.compareError = '';
    let hadFailure = false;
    for (const item of list.slice(0, 5)) {
      try {
        const data = await this.api.shareGet(this.festivalId, item.alias);
        const map: Record<string, { rating?: number; note?: string }> = {};
        for (const r of data) map[r.bandId] = { rating: r.rating, note: r.note };
        this.comparisons.push({ alias: item.alias, label: item.label, map });
      } catch (e: any) {
        hadFailure = true; // still continue with others
      }
    }
    if (hadFailure) {
      // Persist only the successfully loaded items
      this.persistComparisons();
      // No technical details for user; keep message friendly and brief
      this.compareError = 'Ein gespeicherter Vergleich konnte nicht geladen werden und wurde übersprungen.';
      this.toasts.info(this.compareError);
    }
    this.scheduleInitTooltips();
  }

  async addComparison() {
    if (!this.compareAliasInput || this.comparisons.length >= 5) return;
    try {
      this.compareError = '';
      const alias = this.compareAliasInput.trim();
      const label = (this.compareLabelInput || '').trim() || undefined;
      // Duplikate vermeiden
      if (this.comparisons.some(c => c.alias === alias)) {
        this.compareError = 'Alias bereits hinzugefügt';
        this.toasts.error(this.compareError);
        return;
      }
      const data = await this.api.shareGet(this.festivalId, alias);
      const map: Record<string, { rating?: number; note?: string }> = {};
      for (const r of data) map[r.bandId] = { rating: r.rating, note: r.note };
      this.comparisons.push({ alias, label, map });
      this.persistComparisons();
      this.compareAliasInput = '';
      this.compareLabelInput = '';
      this.scheduleInitTooltips();
    } catch (e: any) {
      const msg = String(e?.message || '').toLowerCase();
      if (msg.includes('404') || msg.includes('not found')) {
        this.compareError = 'Kein Vergleich unter diesem Alias gefunden. Bitte überprüfe den Alias. Gehört dieser Alias zu einem anderen Festival?';
      } else {
        this.compareError = 'Vergleich konnte nicht geladen werden. Bitte Alias prüfen und erneut versuchen.';
      }
      this.toasts.error(this.compareError);
    }
  }

  removeComparison(idx: number) {
    this.comparisons.splice(idx, 1);
    this.persistComparisons();
    this.scheduleInitTooltips();
  }

  clearAllComparisons() {
    this.comparisons = [];
    this.compareAliasInput = '';
    this.compareLabelInput = '';
    try {
      localStorage.removeItem(`fb-compare-list-${this.festivalId}`);
    } catch {}
    this.scheduleInitTooltips();
  }

  hasComparison() {
    return this.comparisons.length > 0;
  }

  private scheduleInitTooltips() {
    // Initialisierung nach DOM-Updates
    this.zone.runOutsideAngular(() => {
      setTimeout(() => {
        this.destroyTooltips();
        const host = this.elRef.nativeElement;
        const nodes = host.querySelectorAll('[data-bs-toggle="tooltip"][title]');
        nodes.forEach((node) => {
          const title = (node as HTMLElement).getAttribute('title');
          if (title && title.trim().length > 0) {
            const t = new Tooltip(node as HTMLElement, { container: 'body', placement: 'top', trigger: 'hover focus' });
            this.tooltipInstances.push(t);
          }
        });
      });
    });
  }

  private destroyTooltips() {
    for (const t of this.tooltipInstances) {
      try { t.dispose(); } catch {}
    }
    this.tooltipInstances = [];
  }

  private persistComparisons() {
    try {
      const list = this.comparisons.map(c => ({ alias: c.alias, label: c.label }));
      localStorage.setItem(`fb-compare-list-${this.festivalId}`, JSON.stringify(list));
    } catch {}
  }

  toggleSettings() {
    this.showSettings = !this.showSettings;
  }

  ratingClass(r?: number) {
    if (!r) return '';
    return `rating-${r}`;
  }

  ratingBadgeClass(r?: number) {
    if (!r) return '';
    return `rating-${r}`;
  }

  compShort(c: { label?: string; alias: string }) {
    const base = (c.label || c.alias || 'V').trim();
    const short = base.slice(0, 2);
    return `${short || 'V'}.`;
  }

  openSpotifyArtist(event: MouseEvent, artistId: string) {
    // Standardverhalten bei Modifikatortasten/Mittelklick erlauben
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button === 1) return;
    event.preventDefault();
    const uri = `spotify:artist:${artistId}`;
    const web = `https://open.spotify.com/artist/${artistId}`;

    let appLikelyOpened = false;
    const onVis = () => { if (document.hidden) appLikelyOpened = true; };
    const onBlur = () => { appLikelyOpened = true; };
    const onPageHide = () => { appLikelyOpened = true; };
    document.addEventListener('visibilitychange', onVis, { once: true });
    window.addEventListener('blur', onBlur, { once: true });
    window.addEventListener('pagehide', onPageHide, { once: true });

    try {
      // Direktnavigation ist in Desktop-Browsern am zuverlässigsten
      window.location.href = uri;
      // Safari-Fallback: verstecktes Iframe
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = uri;
      document.body.appendChild(iframe);
      setTimeout(() => { try { document.body.removeChild(iframe); } catch {} }, 2000);
    } catch {}

    // Fallback nach 4s: reduziert Doppel-Öffnungen, wenn die App etwas
    // länger zum Aktivieren braucht
    setTimeout(() => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('pagehide', onPageHide);
      if (!appLikelyOpened) {
        // Fallback: Spotify Web in neuem Tab öffnen
        let w: Window | null = null;
        try { w = window.open(web, '_blank', 'noopener,noreferrer'); } catch {}
        if (!w) {
          this.toasts.info('Konnte Spotify-Web nicht in neuem Tab öffnen. Bitte Pop-up zulassen und erneut versuchen.');
        }
      }
    }, 4000);
  }

  goBack() { this.location.back(); }

  onSelectBlur(evt: Event) {
    try { (evt.target as HTMLSelectElement)?.blur(); } catch {}
  }
}
